import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';

const AuthScreen = () => {
  const { login, register, error } = useAuth();
  const { setCurrentScreen } = useApp();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        if (formData.name.length < 2) {
          setFormError('Name must be at least 2 characters');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setFormError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        result = await register(formData.email, formData.password, formData.name);
      }

      if (result.success) {
        setCurrentScreen('home');
      } else {
        setFormError(result.error || 'An error occurred');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-black/10 px-6 py-5">
        <button
          onClick={() => setCurrentScreen('home')}
          className="flex items-center gap-2 text-black/72 hover:text-black transition-colors mb-4"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          <span className="text-xs font-light tracking-wider">Back</span>
        </button>
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-1">
          {isLogin ? 'Welcome Back' : 'Join Us'}
        </p>
        <h1 className="font-sans text-2xl font-light text-black">
          {isLogin ? 'Sign In' : 'Create Account'}
        </h1>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-8">
        <div className="premium-card rounded-3xl p-8 luxury-border max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-xs text-gray-500 mb-2 font-light tracking-wide">
                  Full Name
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50/[0.02] border border-[#000000]/20 
                      rounded-xl text-sm focus:outline-none focus:border-[#000000]/30 focus:ring-1 
                      focus:ring-[#000000]/10 transition-all"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-500 mb-2 font-light tracking-wide">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50/[0.02] border border-[#000000]/20 
                    rounded-xl text-sm focus:outline-none focus:border-[#000000]/30 focus:ring-1 
                    focus:ring-[#000000]/10 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-2 font-light tracking-wide">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3.5 bg-gray-50/[0.02] border border-[#000000]/20 
                    rounded-xl text-sm focus:outline-none focus:border-[#000000]/30 focus:ring-1 
                    focus:ring-[#000000]/10 transition-all"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-[10px] text-gray-400 mt-1.5">Minimum 6 characters</p>
              )}
            </div>

            {(formError || error) && (
              <div className="p-3 bg-[#B85C3B]/10 border border-[#B85C3B]/20 rounded-lg">
                <p className="text-xs text-[#B85C3B]">{formError || error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#000000] hover:bg-[#000000] text-white py-4 px-6 rounded-xl
                flex items-center justify-center gap-3 transition-all duration-300
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <span className="text-sm font-semibold tracking-[0.1em] uppercase">
                  {isLogin ? 'Sign In' : 'Create Account'}
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormError('');
                }}
                className="ml-2 text-gray-900 hover:text-[#600018] font-medium"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-8 max-w-md mx-auto">
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-4 text-center">
            Why Create an Account?
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Save Scan History', desc: 'Access your past scans anytime' },
              { label: 'Build Wishlist', desc: 'Save alternatives you love' },
              { label: 'Track Favorites', desc: 'Mark and revisit garments' },
              { label: 'Sync Across Devices', desc: 'Your data, everywhere' }
            ].map((benefit, i) => (
              <div key={i} className="p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                <p className="text-xs font-medium text-gray-900">{benefit.label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
